import { css } from '@codemirror/lang-css';
import { html } from '@codemirror/lang-html';
import { javascript } from '@codemirror/lang-javascript';
import { json } from '@codemirror/lang-json';
import { markdown } from '@codemirror/lang-markdown';
import { HighlightStyle } from '@codemirror/language';
import type { Extension } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { tags as t } from '@lezer/highlight';

/**
 * Shared CodeMirror styling and language detection.
 *
 * Lives in `ui/` because more than one feature renders code now — the session Files tab (read-only)
 * and the skill editor (editable). Both must look identical; two copies of a themed CodeMirror
 * setup would drift the moment a design token changed.
 */

/** UI chrome (background, gutters, selection, cursor) themed off our design tokens
 * so the editor matches both light and dark mode instead of a fixed third-party palette. */
export const codeTheme = EditorView.theme({
  '&': {
    backgroundColor: 'var(--color-surface-1)',
    color: 'var(--color-fg)',
    height: '100%',
  },
  '.cm-content': {
    caretColor: 'var(--color-brand)',
  },
  '.cm-cursor, .cm-dropCursor': {
    borderLeftColor: 'var(--color-brand)',
  },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection': {
    backgroundColor: 'var(--color-brand-tint)',
  },
  '.cm-activeLine, .cm-activeLineGutter': {
    backgroundColor: 'var(--color-surface-2)',
  },
  '.cm-gutters': {
    backgroundColor: 'var(--color-surface-1)',
    color: 'var(--color-fg-tertiary)',
    border: 'none',
    borderRight: '1px solid var(--color-line)',
  },
  '.cm-matchingBracket, .cm-nonmatchingBracket': {
    backgroundColor: 'var(--color-surface-3)',
    outline: 'none',
  },
});

export const codeHighlightStyle = HighlightStyle.define([
  { tag: t.comment, color: 'var(--color-fg-tertiary)', fontStyle: 'italic' },
  { tag: [t.keyword, t.controlKeyword, t.moduleKeyword, t.operatorKeyword], color: 'var(--color-brand-text)' },
  { tag: [t.string, t.special(t.string)], color: 'var(--color-diff-add)' },
  { tag: [t.number, t.bool, t.null], color: 'var(--color-warning)' },
  { tag: [t.function(t.variableName), t.function(t.propertyName)], color: 'var(--color-info)' },
  { tag: [t.typeName, t.className, t.namespace], color: 'var(--color-purple-500)' },
  { tag: t.tagName, color: 'var(--color-brand-text)' },
  { tag: t.attributeName, color: 'var(--color-warning)' },
  { tag: [t.operator, t.punctuation, t.angleBracket], color: 'var(--color-fg-secondary)' },
  { tag: t.invalid, color: 'var(--color-danger)', textDecoration: 'underline' },
]);

/**
 * The CodeMirror language mode for a path, or `null` when we have no package for it.
 *
 * Only the languages with a first-party package are highlighted; anything else (`.rs`, `.py`,
 * `.sh`) renders as plain monospace rather than pulling in a legacy-mode grab-bag for partial
 * coverage.
 */
export function languageExtension(path: string): Extension | null {
  const ext = path.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'js':
    case 'jsx':
      return javascript({ jsx: true });
    case 'ts':
    case 'tsx':
      return javascript({ jsx: true, typescript: true });
    case 'json':
      return json();
    case 'css':
      return css();
    case 'html':
      return html();
    case 'md':
    case 'markdown':
      return markdown();
    default:
      return null;
  }
}
